import { createTheme, ThemeOptions } from '@mui/material/styles';
import { buttonClasses, outlinedInputClasses } from '@mui/material';
import type { Theme } from '@mui/material/styles';

// Ported from ComplyControl's theme files — two layers, both read-only
// reference at complycontrol/frontend/src/{baseTheme,adminPanelTheme}.ts:
//   1. baseTheme.ts: the shared tokens (palette/typography/breakpoints).
//   2. adminPanelTheme.ts: the Admin-Panel COMPONENT overrides that spread
//      baseThemeOptions and match this kit's Admin-Panel Figma frames.
// Only the component overrides adminPanelTheme.ts defines for MUI components
// this kit actually uses are ported (verbatim, by CC line number in comments
// below). RS-only additions (not present in either CC file) are marked below.
declare module '@mui/material/styles' {
  interface TypeText {
    white: string;
    white50: string;
    // Admin-Panel additions (adminPanelTheme.ts:97-104) — only the fields the
    // ported MuiButton/MuiChip overrides below actually read. `tertiary`,
    // `success` and `yellow` exist in CC's palette too but aren't referenced
    // by anything ported here, so they're dropped as dead tokens (see task
    // report for the full list of CC tokens deliberately not ported).
    accent: string;
    blue: string;
    blueHover: string;
    bluePressed: string;
  }
  interface Palette {
    // Admin-Panel addition (adminPanelTheme.ts:106-119), trimmed to the
    // fields MuiChip's color variants below actually read.
    fill: {
      blue: string;
      blueLight: string;
      redLight: string;
      column: string;
    };
  }
  interface PaletteOptions {
    fill?: {
      blue: string;
      blueLight: string;
      redLight: string;
      column: string;
    };
  }
}

// Admin-Panel addition (adminPanelTheme.ts:11-16) — needed to type-check the
// MuiButton `variant: 'critical'` entry in the styleOverrides ported below.
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    critical: true;
  }
}

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: { main: '#0E0E0E' },
    secondary: { main: '#97a2aa' },
    error: { main: '#FC4D2B', light: '#FDEFED' },
    success: { main: '#079455', light: '#ECFDF3' },
    warning: { main: '#F07C29', light: '#FEEED7' },
    divider: '#97A2AA',
    info: { main: '#01ADEF' },
    text: {
      primary: '#0E0E0E',
      secondary: '#97A2AA',
      white: '#FFFFFF',
      white50: 'rgba(255, 255, 255, 0.5)',
      // Admin-Panel additions (adminPanelTheme.ts:97-104), see interface
      // comment above for what was deliberately left out.
      accent: '#FC4D2B',
      blue: '#01ADEF',
      blueHover: '#26B6F0',
      bluePressed: '#0197D4',
    },
    // CC's adminPanelTheme.ts sets action.hover to the solid brand blue
    // (#01ADEF) — that value is CC-side dead weight (nothing in CC actually
    // renders a hover using this token) but here it is live: MUI itself reads
    // palette.action.hover as a background-fill color, e.g. MenuItem's
    // `&:hover { backgroundColor: palette.action.hover }` and
    // TableRow's `&.MuiTableRow-hover:hover`. Porting the blue verbatim paints
    // a solid brand-blue block behind near-black text on every hovered menu
    // item and table row (visible in the kit-actions-menu and
    // kit-date-range-picker baselines). Deliberately diverging: use a subtle
    // neutral fill (the palette's own #0E0E0E at 4% alpha, matching MUI's own
    // default hover convention) instead. The blue accent CC actually wants is
    // still available via text.blue / fill.blue for components that opt in.
    action: { hover: 'rgba(14, 14, 14, 0.04)' },
    // Admin-Panel addition (adminPanelTheme.ts:106-119), trimmed — see
    // interface comment above.
    fill: {
      blue: '#01ADEF',
      blueLight: '#E7F6FE',
      redLight: '#FDEFED',
      column: '#34405408',
    },
  },
  // RS addition: CC sets the body font via Tailwind; here the theme carries it.
  typography: { fontFamily: 'TT Interphases Pro Medium' },
  components: {
    // Admin-Panel override (adminPanelTheme.ts:174-183), merged with the
    // RS/base `input` placeholder rule below (baseTheme.ts:46-55) — the two
    // don't conflict (different styleOverrides sub-keys: `root` vs `input`),
    // so both are kept rather than one replacing the other.
    MuiInputBase: {
      styleOverrides: {
        input: {
          '::placeholder': { color: '#97A2AA', opacity: 0.5 },
        },
        root: {
          fontFamily: 'TT Interphases Pro Regular',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '140%',
        },
      },
    },
    // RS addition: kept as-is. adminPanelTheme.ts has no MuiTypography
    // override that matches our numbers (its h1/h3/h5 are 36/36/20px vs our
    // 48/20/18px) and no Figma node was given to justify re-scaling
    // typography app-wide, so this is left untouched pending a dedicated
    // typography pass with the designer.
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: 'TT Interphases Pro Medium' },
        caption: {
          fontSize: '10px',
          fontWeight: 400,
          lineHeight: '12px',
          fontFamily: 'TT Interphases Pro Medium',
          color: '#97A2AA',
          textTransform: 'uppercase',
        },
        h1: { fontSize: '48px', fontWeight: 500, lineHeight: '100%', fontFamily: 'TT Interphases Pro Medium' },
        h3: { fontSize: '20px', fontWeight: 500, lineHeight: '130%', fontFamily: 'TT Interphases Pro Medium' },
        h5: { fontSize: '18px', fontWeight: 500, lineHeight: '130%', fontFamily: 'TT Interphases Pro Medium' },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:364-402). Genuinely conflicts
    // with the RS/base version on the same styleOverrides.root sub-keys
    // (border colors, hover opacity) — CC's version wins per the fix brief.
    MuiInput: {
      styleOverrides: {
        root: {
          '& .MuiInputAdornment-root svg path': { stroke: '#97A2AA' },
          ':after': { borderBottom: '1px solid #DEDEDE' },
          '&.Mui-focused': {
            ':after': { borderBottom: '1px solid #0E0E0E' },
            '& .MuiInputAdornment-root svg path': { stroke: '#0E0E0E' },
          },
          '&:hover': {
            ':before': { borderBottom: '1px solid #0E0E0E !important' },
            '& .MuiInputAdornment-root svg path': { stroke: '#0E0E0E' },
          },
          '&.Mui-error': {
            color: '#FC4D2B',
            ':after': { borderBottom: '1px solid #FC4D2B' },
            '&:hover': { ':before': { borderBottom: '1px solid #FC4D2B !important' } },
          },
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:138-173) — the compact boxed
    // `variant="outlined" size="small"` field TagInput's custom-options input
    // now uses (Figma 3339:44965 / 3339:45411 / 1175:37235).
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: '44px',
            fontFamily: 'TT Interphases Pro Medium',
          },
        },
      },
      variants: [
        {
          // Compact white input used across admin forms (e.g., Rule Management fields)
          props: { variant: 'outlined', size: 'small' },
          style: {
            '& .MuiOutlinedInput-root': {
              height: '50px',
              minHeight: '50px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
            },
            '& .MuiOutlinedInput-input': {
              height: '20px',
              lineHeight: '20px',
              fontSize: '14px',
              color: '#0E0E0E',
            },
            '& .MuiSelect-icon': {
              right: '8px',
              top: 'calc(50% - 10px)',
            },
          },
        },
      ],
    },
    // Admin-Panel addition (adminPanelTheme.ts:304-354).
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '140%',
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderWidth: '1px',
            borderColor: '#E7E7E7',
          },
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderWidth: '1px',
            borderColor: '#E7E7E7',
          },
          [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
            borderWidth: '1px',
            borderColor: '#E7E7E7',
          },
          '& .MuiInputAdornment-root .MuiButtonBase-root': {
            height: '16px',
            width: '16px',
            background: 'transparent',
          },
          '& .MuiSvgIcon-root': {
            color: '#A8A8A8',
            cursor: 'pointer',
          },
          '& .MuiSvgIcon-root:hover': {
            color: '#0E0E0E',
          },
          // Multiline inputs get MUI's own ~16x14px padding on the root ON TOP
          // of the 12px `input` padding below; zero the root so textareas get
          // the same 12px as every other input.
          '&.MuiInputBase-multiline': {
            padding: 0,
          },
        },
        input: {
          padding: '12px',
          fontSize: '14px',
          fontWeight: '500',
          lineHeight: '140%',
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:421-456).
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 40,
          color: '#0E0E0E',
          height: '56px',
          padding: '18px',
          fontSize: '16px',
          '&:hover': {
            backgroundColor: '#F0F0F0',
          },
          '&:active': {
            color: '#01ADEF',
            backgroundColor: 'transparent',
          },
          '&[disabled]': {
            color: 'rgba(14, 14, 14, 0.2)',
            backgroundColor: 'transparent',
          },
        },
        sizeSmall: {
          padding: '4px',
          height: '20px',
          width: '20px',
        },
        sizeMedium: {
          padding: '16px',
        },
        sizeLarge: {
          padding: '24px',
        },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:457-699) — conflicts with the
    // RS pill-button approximation on `root.borderRadius`/geometry, CC's
    // version wins per the fix brief.
    //
    // Geometry (root height/minWidth/padding/gap/typography below, plus the
    // `contained`/`outlined` sizeMedium fixes) comes from the Figma Button
    // component, measured directly across four instances/frames: onboarding
    // "Continue" (3339:45055) and "Select All" (3339:45056), ArticleCard
    // "View summary" (2871:49913), and the All Articles toolbar "Refresh"
    // (2871:49908). All four are identical: 44px height, pill radius (already
    // achieved by borderRadius: 40 below), 12px/20px padding, 4px label/icon
    // gap, 14px/500/20px type — and every instance is Width: Hug with NO
    // minimum width. CC's Admin-Panel-specific 48px height and 128px minWidth
    // are deliberately NOT ported — do not restore them.
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        variant: 'contained',
      },
      variants: [
        {
          props: { variant: 'critical' },
          style: {
            background: '#F7F7F7',
            color: '#E11314',
            padding: '12px 20px',
            fontSize: '14px',
            lineHeight: '140%',
            gap: '8px',
            '&:hover': {
              background: '#F0F0F0',
              color: '#E11314',
            },
            '&:active': {
              background: '#F7F7F7',
              color: '#E11314',
            },
            '&[disabled]': {
              background: 'rgba(14, 14, 14, 0.05)',
              color: 'rgba(14, 14, 14, 0.2)',
              cursor: 'default',
            },
          },
        },
        {
          props: { size: 'small' },
          style: {
            height: '32px',
          },
        },
      ],
      styleOverrides: {
        root: {
          fontFamily: 'TT Interphases Pro Medium',
          whiteSpace: 'nowrap',
          height: '44px',
          minWidth: 0,
          padding: '12px 20px',
          gap: '4px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          borderRadius: 40,
          textTransform: 'none',
          background: '#0E0E0E',
          color: '#FFFFFF',
          '&:hover': {
            background: '#262626',
            color: '#E4F6FE',
          },
          '&:active': {
            background: '#262626',
            color: '#E4F6FE',
          },
          '&[disabled]': {
            background: '#0E0E0E',
            color: '#FFFFFF',
            opacity: 0.5,
          },
        },
        contained: {
          [`&.${buttonClasses.contained}.${buttonClasses.sizeLarge}`]: {
            padding: '12px 32px',
            fontSize: '16px',
            gap: '16px',
          },
          // Kept in sync with root's Figma-measured values (44px/12px-20px/
          // 4px/14px/20px/500) — sizeMedium is the only size this kit's
          // Button ever renders (no consumer passes size="large"/"small"),
          // so this sub-rule must not reintroduce a different line-height.
          [`&.${buttonClasses.contained}.${buttonClasses.sizeMedium}`]: {
            padding: '12px 20px',
            fontSize: '14px',
            lineHeight: '20px',
            gap: '4px',
          },
          '&[disabled]': {
            background: '#0E0E0E',
            color: '#FFFFFF',
            opacity: 0.5,
          },
          '&:hover': {
            background: '#262626',
            color: '#E4F6FE',
          },
          '&:active': {
            background: '#262626',
            color: '#000000',
          },
          '&.MuiButton-containedError': {
            backgroundColor: '#FC4D2B',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#F74826',
              color: '#FFFFFF',
            },
            '&:active': {
              backgroundColor: '#F24321',
              color: '#FFFFFF',
            },
            '&[disabled]': {
              backgroundColor: '#F7F7F7',
              color: 'rgba(138, 138, 139, 0.4)',
            },
          },
        },
        containedSecondary: {
          background: '#F7F7F7',
          lineHeight: '20px',
          color: '#01ADEF',
          '&:hover': {
            background: '#F0F0F0',
            color: '#01ADEF',
          },
          '&:active': {
            background: '#F7F7F7',
            color: '#01ADEF',
          },
          '&[disabled]': {
            background: '#F7F7F7',
            color: '#01ADEF',
            opacity: 0.5,
          },
        },
        outlined: {
          borderColor: '#97A2AA',
          background: 'transparent',
          color: '#97A2AA',
          // `outlined` isn't rendered by this kit's Button wrapper today, but
          // its sizeMedium sub-rule used to hard-code CC's 54px — since
          // sizeMedium is the size any default-size MUI Button gets
          // (including a future raw `variant="outlined"` consumer), that
          // would silently reintroduce a non-Figma height. Pinned to the
          // root's 44px instead; padding/fontSize/gap are CC's own and left
          // as-is since no outlined instance was in the measured Figma set.
          [`&.${buttonClasses.outlined}.${buttonClasses.sizeMedium}`]: {
            height: '44px',
            padding: '16px 32px',
            fontSize: '16px',
            gap: '16px',
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.sizeSmall}`]: {
            height: '44px',
            padding: '12px 20px',
            fontSize: '14px',
            gap: '8px',
          },
          '&:hover': {
            borderColor: '#01ADEF',
            background: '#F0F0F0',
            color: '#01ADEF',
          },
          '&:active': {
            borderColor: '#01ADEF',
            background: 'transparent',
            color: '#01ADEF',
          },
          '&[disabled]': {
            borderColor: '#97A2AA',
            background: 'transparent',
            color: '#97A2AA',
            opacity: 0.5,
          },
        },
        outlinedSecondary: {
          color: '#0E0E0EA3',
          borderRadius: 0,
          borderColor: 'transparent',
          borderBottom: '2px solid #E7E7E7',
          '&:hover': {
            backgroundColor: '#F0F0F0',
            borderColor: 'transparent',
            borderBottom: '2px solid #01ADEF',
          },
          '&:active': {
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderBottom: '2px solid #01ADEF',
          },
          '&.Mui-selected': {
            color: '#0E0E0E',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            borderBottom: '2px solid #01ADEF',
          },
        },
        text: {
          borderColor: 'transparent',
          background: '#FFFFFF',
          color: '#0E0E0E',
          padding: '12px 20px',
          '&:hover': {
            borderColor: '#F0F0F0',
            background: '#F0F0F0',
            color: '#0E0E0E',
          },
          '&:active': {
            borderColor: '#F7F7F7',
            background: '#F7F7F7',
            color: '#0E0E0E',
          },
          '&[disabled]': {
            borderColor: 'transparent',
            background: 'transparent',
            color: '#0E0E0E',
          },
        },
        textSecondary: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.text.blue,
          borderRadius: 0,
          border: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.text.blueHover,
            borderRadius: 0,
          },
          '&:active': {
            backgroundColor: 'transparent',
            color: theme.palette.text.bluePressed,
            borderRadius: 0,
          },
        }),
        containedError: {
          background: '#FDEFED',
          color: '#FC4D2B',
          '&:hover': {
            background: '#FFF6F4',
            color: '#FC4D2B',
          },
          '&:active': {
            background: '#FFE9E6',
            color: '#FC4D2B',
          },
          '&[disabled]': {
            background: '#989AA21F',
            color: '#8A8A8B66',
            opacity: 0.5,
          },
        },
        endIcon: {
          display: 'initial',
          marginLeft: '4px',
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:732-738).
    MuiTable: {
      styleOverrides: {
        root: {
          fontFamily: 'TT Interphases Pro Regular',
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:739-747). MuiTableHead
    // (adminPanelTheme.ts:748-761) is deliberately NOT ported: its selector
    // targets `.Mui-TableHeadCell-Content`, a material-react-table class our
    // plain MUI <DataTable> doesn't use — porting it would be a no-op.
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'TT Interphases Pro Regular',
          padding: '16px 2px',
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:762-783).
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          color: '#E7E7E7',
          borderRadius: 0,
          '& .MuiTouchRipple-root': {
            borderRadius: 0,
          },
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:not(.Mui-checked):hover .MuiSvgIcon-root rect': {
            fill: 'rgba(52, 64, 84, 0.03)',
          },
          '&.Mui-checked': {
            color: '#01ADEF',
          },
        },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:831-878) — fixes the black
    // checked-track Toggle bug (Figma sources table, node 3095:52781).
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#01ADEF',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#33cf4d',
              border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: '#97A2AA',
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.7,
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
          },
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
        },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:932-938) — fixes Modal's
    // 12px-vs-8px radius mismatch (Figma modal, node 4036:85796).
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
        },
      },
    },
    // Admin-Panel addition (adminPanelTheme.ts:961-1056) — used by TagInput's
    // chips (default/no-color variant; the color variants below are ported
    // for verbatim fidelity even though this kit doesn't currently pass a
    // `color` prop to any Chip).
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#0E0E0E',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: '500',
          borderRadius: '4px',
          padding: '4px',
        },
        icon: {
          color: '#ffffff',
        },
        label: {
          color: '#ffffff',
        },
        deleteIcon: {
          color: '#ffffff',
          '&:hover': {
            color: '#8A8A8B',
          },
        },
      },
      variants: [
        {
          props: { color: 'primary' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette?.fill?.blueLight,
            '& .MuiChip-label': {
              color: theme.palette?.fill?.blue,
            },
            '& .MuiChip-icon': {
              color: theme.palette?.fill?.blue,
            },
            '& .MuiChip-deleteIcon': {
              color: theme.palette?.fill?.blue,
            },
          }),
        },
        {
          props: { color: 'error' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette?.fill?.redLight,
            '& .MuiChip-label': {
              color: theme.palette?.text?.accent,
            },
            '& .MuiChip-icon': {
              color: theme.palette?.text?.accent,
            },
            '& .MuiChip-deleteIcon': {
              color: theme.palette?.text?.accent,
            },
          }),
        },
        {
          props: { color: 'warning' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette?.warning?.light,
            '& .MuiChip-label': {
              color: theme.palette?.warning?.main,
            },
            '& .MuiChip-icon': {
              color: theme.palette?.warning?.main,
            },
            '& .MuiChip-deleteIcon': {
              color: theme.palette?.warning?.main,
            },
          }),
        },
        {
          props: { color: 'success' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette?.success?.light,
            '& .MuiChip-label': {
              color: theme.palette?.success?.main,
            },
            '& .MuiChip-icon': {
              color: theme.palette?.success?.main,
            },
            '& .MuiChip-deleteIcon': {
              color: theme.palette?.success?.main,
            },
          }),
        },
        {
          props: { color: 'secondary' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette?.fill?.column,
            '& .MuiChip-label': {
              color: theme.palette?.text?.primary,
            },
          }),
        },
      ],
    },
    // Admin-Panel override (adminPanelTheme.ts:1083-1090) — fixes Modal's
    // default elevation-24 drop shadow (Figma modal, node 4036:85796).
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:1091-1098) — fixes
    // ActionsMenu's hand-rolled shadow.
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          boxShadow: '0 -1px 2px rgba(0,0,0,0.02), 0 4px 9px rgba(0,0,0,0.05)',
        },
      },
    },
    // Admin-Panel override (adminPanelTheme.ts:1099-1106) — fixes
    // DateRangePicker's hand-rolled shadow.
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          boxShadow: '0 -1px 2px rgba(0,0,0,0.02), 0 4px 9px rgba(0,0,0,0.05)',
        },
      },
    },
    // RS addition: dark tooltip per Figma (3095:52302). Kept as-is —
    // verified adminPanelTheme.ts (and baseTheme.ts) define no MuiTooltip
    // override at all, so there is nothing from CC to adopt here.
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0E0E0E',
          fontSize: '12px',
          fontWeight: 500,
          borderRadius: '6px',
          padding: '8px 12px',
        },
        arrow: { color: '#0E0E0E' },
      },
    },
  },
  breakpoints: {
    values: { xs: 0, sm: 768, md: 1200, lg: 1920, xl: 1920 },
  },
};

export const theme = createTheme(themeOptions);
